import React, { useState } from 'react';
import {
  AppBar,
  Box,
  CssBaseline,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Button,
  Divider,
  Collapse,
  Badge,
} from '@mui/material';
import {
  ExpandLess,
  ExpandMore,
} from '@mui/icons-material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Business as BusinessIcon,
  Devices as DevicesIcon,
  MedicalServices as EquipmentIcon,
  Sensors as BarriersIcon,
  Store as SuppliersIcon,

  Person as PersonIcon,
  Security as SecurityIcon,
  NotificationsActive as AlertsIcon,
  Warning as AlarmsIcon,
  LocationOn as LocationIcon,
  Analytics as AnalyticsIcon,
  TrackChanges as PatientTrackingIcon,
  Badge as StaffTrackingIcon,
  Room as ZoneManagementIcon,
  Map as InteractiveMapsIcon,
  Link as LinkIcon,
  Logout as LogoutIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useMenuBadges } from '../../hooks/useMenuBadges';
import Breadcrumb from './Breadcrumb';

const drawerWidth = 240;

const getMenuGroups = (t, userRole, isSuperAdmin) => {
  const menuGroups = [
    {
      title: 'Principal',
      items: [
        { text: t('nav.dashboard'), icon: <DashboardIcon />, path: '/dashboard', roles: ['super_admin', 'admin', 'manager', 'technician'] },
      ]
    },
    {
      title: 'Administração',
      items: [
        { text: t('nav.hospitals'), icon: <BusinessIcon />, path: '/hospitals', roles: ['super_admin'] },
        { text: t('nav.users'), icon: <PersonIcon />, path: '/users', roles: ['super_admin', 'admin'] },
      ]
    },
    {
      title: 'Infraestrutura',
      items: [
        { text: t('nav.devices'), icon: <DevicesIcon />, path: '/devices', roles: ['super_admin', 'admin', 'manager'] },
      ]
    },
    {
      title: 'Gestão de Ativos',
      items: [
        { text: t('nav.equipment'), icon: <EquipmentIcon />, path: '/equipment', roles: ['super_admin', 'admin', 'manager'] },
        { text: t('nav.suppliers'), icon: <SuppliersIcon />, path: '/suppliers', roles: ['super_admin', 'admin', 'manager'] },
      ]
    },
    {
      title: 'Rastreamento RTLS',
      items: [
        { text: t('nav.assetTracking'), icon: <LocationIcon />, path: '/asset-tracking', roles: ['super_admin', 'admin', 'manager', 'technician'] },
        { text: t('nav.patientTracking'), icon: <PatientTrackingIcon />, path: '/patient-tracking', roles: ['super_admin', 'admin', 'manager', 'technician'] },
        { text: t('nav.staffTracking'), icon: <StaffTrackingIcon />, path: '/staff-tracking', roles: ['super_admin', 'admin', 'manager', 'technician'] },
        { text: t('nav.barriers'), icon: <BarriersIcon />, path: '/barriers', roles: ['super_admin', 'admin', 'manager'] },
        { text: t('nav.equipmentBarriers'), icon: <LinkIcon />, path: '/equipment-barriers', roles: ['super_admin', 'admin', 'manager'] },
      ]
    },
    {
      title: 'Mapas & Zonas',
      items: [
        { text: t('nav.interactiveMaps'), icon: <InteractiveMapsIcon />, path: '/interactive-maps', roles: ['super_admin', 'admin', 'manager'] },
        { text: t('nav.zoneManagement'), icon: <ZoneManagementIcon />, path: '/zone-management', roles: ['super_admin', 'admin', 'manager'] },
      ]
    },
    {
      title: 'Monitoramento',
      items: [
        { text: t('nav.alerts'), icon: <AlertsIcon />, path: '/alerts', roles: ['super_admin', 'admin', 'manager', 'technician'], badge: 'alerts' },
        { text: t('nav.alarms'), icon: <AlarmsIcon />, path: '/alarms', roles: ['super_admin', 'admin', 'manager', 'technician'], badge: 'alarms' },
      ]
    },
    {
      title: 'Relatórios',
      items: [
        { text: t('nav.analytics'), icon: <AnalyticsIcon />, path: '/analytics', roles: ['super_admin', 'admin', 'manager'] },
        { text: t('nav.auditLogs'), icon: <SecurityIcon />, path: '/audit-logs', roles: ['super_admin', 'admin'] },
      ]
    },
  ];
  
  return menuGroups.map(group => ({
    ...group,
    items: group.items.filter(item => item.roles.includes(userRole))
  })).filter(group => group.items.length > 0);
};

function Layout({ children }) {
  const { t } = useTranslation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState({
    'Principal': true,
    'Rastreamento RTLS': true,
    'Monitoramento': true
  });
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, userRole, isSuperAdmin, user } = useAuth();
  const menuGroups = getMenuGroups(t, userRole, isSuperAdmin);
  const badges = useMenuBadges();
  
  const handleGroupToggle = (groupTitle) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupTitle]: !prev[groupTitle]
    }));
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleLogout = () => {
    logout();
  };

  const drawer = (
    <div>
      <Toolbar>
        <Typography variant="h6" noWrap component="div">
         <img src="/logo.png" alt="Logo" style={{ maxHeight: '40px', maxWidth: '160px' }} />
        </Typography>
      </Toolbar>
      <List sx={{ width: '100%', bgcolor: 'background.paper' }}>
        {menuGroups.map((group, groupIndex) => (
          <React.Fragment key={group.title}>
            {group.items.length === 1 ? (
              // Grupo com apenas 1 item - renderiza direto
              <ListItem disablePadding>
                <ListItemButton
                  selected={location.pathname === group.items[0].path}
                  onClick={() => navigate(group.items[0].path)}
                  sx={{ pl: 2 }}
                >
                  <ListItemIcon>{group.items[0].icon}</ListItemIcon>
                  <ListItemText primary={group.items[0].text} />
                  {group.items[0].badge && badges[group.items[0].badge] > 0 && (
                    <Badge 
                      badgeContent={badges[group.items[0].badge]} 
                      color="error" 
                      sx={{ mr: 1 }}
                    />
                  )}
                </ListItemButton>
              </ListItem>
            ) : (
              // Grupo com múltiplos itens - renderiza com collapse
              <>
                <ListItemButton 
                  onClick={() => handleGroupToggle(group.title)}
                  sx={{ 
                    bgcolor: 'action.hover',
                    '&:hover': { bgcolor: 'action.selected' }
                  }}
                >
                  <ListItemText 
                    primary={group.title} 
                    primaryTypographyProps={{
                      variant: 'subtitle2',
                      fontWeight: 600,
                      color: 'text.secondary'
                    }}
                  />
                  {expandedGroups[group.title] ? <ExpandLess /> : <ExpandMore />}
                </ListItemButton>
                <Collapse in={expandedGroups[group.title]} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {group.items.map((item) => (
                      <ListItem key={item.text} disablePadding>
                        <ListItemButton
                          selected={location.pathname === item.path}
                          onClick={() => navigate(item.path)}
                          sx={{ pl: 4 }}
                        >
                          <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
                          <ListItemText 
                            primary={item.text}
                            primaryTypographyProps={{ variant: 'body2' }}
                          />
                          {item.badge && badges[item.badge] > 0 && (
                            <Badge 
                              badgeContent={badges[item.badge]} 
                              color="error" 
                              sx={{ mr: 1 }}
                            />
                          )}
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                </Collapse>
              </>
            )}
            {groupIndex < menuGroups.length - 1 && <Divider sx={{ my: 1 }} />}
          </React.Fragment>
        ))}
      </List>
    </div>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            Sistema RTLS {isSuperAdmin && '(Super Admin)'}
          </Typography>
          {user && (
            <Typography variant="body2" sx={{ mr: 2, opacity: 0.8 }}>
              {user.name} - {userRole}
            </Typography>
          )}
          <Button color="inherit" onClick={handleLogout} startIcon={<LogoutIcon />}>
            {t('nav.logout')}
          </Button>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
        }}
      >
        <Toolbar />
        <Breadcrumb />
        {children}
      </Box>
    </Box>
  );
}

export default Layout;